import { ChangeDetectionStrategy, Component, computed, inject, input, output } from "@angular/core";

import {
  LktSidebarMenuComponent,
  NavbarMenuItemType,
  ViewSwitchSegment
} from "@frontends-v3/design-system";
import { NzMessageService } from "ng-zorro-antd/message";
import { NzModalService } from "ng-zorro-antd/modal";

import { ProfileConstants } from "../../constants/profile.constants";
import {
  featureAvailability,
  withOfflineMenuAvailability
} from "../../offline/offline-policy.util";
import { NotificationsStateService } from "../../services/notifications/notifications-state.service";
import { ThemeStateService } from "../../services/theme/theme-state.service";
import { AppId, resolveAppUrl } from "../../utils/app-url.util";

import { UserAccountBaseDirective } from "./user-account-base.directive";

/**
 * Alias de `AppId` (`app-url.util.ts`), preservado porque é o nome que os shells já importam. São o
 * MESMO conceito: manter duas definições literais idênticas era um dos três vocabulários paralelos
 * para "qual app" que a DEV-991 encontrou.
 */
export type AppSystem = AppId;

/** Segmentos fixos do alternador de visão (Administração ⇄ Plataforma). */
const VIEW_SWITCH_SEGMENTS: ViewSwitchSegment[] = [
  { id: "admin", label: "Administração", icon: "grid" },
  { id: "platform", label: "Plataforma", icon: "graduation-cap" }
];

@Component({
  selector: "app-shared-menu",
  imports: [LktSidebarMenuComponent],
  providers: [NzModalService, NzMessageService],
  templateUrl: "./app-menu.component.html",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppSharedMenuComponent extends UserAccountBaseDirective {
  private readonly themeState = inject(ThemeStateService);
  private readonly notificationsState = inject(NotificationsStateService);

  public readonly menuItems = input.required<NavbarMenuItemType[]>();
  public readonly appVersion = input.required<string>();

  /** Sistema em que este menu está rodando; define o segmento ativo do viewswitch. */
  public readonly currentApp = input<AppSystem>("platform");

  public readonly isCollapsed = input<boolean>(false);
  public readonly logoSrc = input<string | undefined>(undefined);
  public readonly logoAlt = input<string>("Logo");
  public readonly logoRoute = input<string>("/");
  public readonly dataCy = input<string>("vertical-menu");

  /**
   * O gate é o PAPEL, não o app: notificações existem na Plataforma e no Admin, e a allowlist
   * espelha a das rotas do backend. `available()` fica falso quando o backend respondeu que a
   * feature não existe para esta sessão (403 de papel ou 404 de produto — guarda do CA 14).
   */
  public readonly showNotifications = computed<boolean>(
    () =>
      // DEV-966 (CA 09): notificações pausadas com o Modo Offline ligado.
      featureAvailability("notifications", this.offlineState.enabled()) === "allowed" &&
      this.notificationsState.available() &&
      ProfileConstants.NOTIFICATION_PROFILES.includes(this.authService.authProfile())
  );

  /** DEV-966 (CA 05): com o modo ligado os demais destinos ficam visíveis, mas desabilitados. */
  public readonly visibleMenuItems = computed<NavbarMenuItemType[]>(() =>
    withOfflineMenuAvailability(this.menuItems(), this.offlineState.enabled())
  );

  public readonly notificationsUnreadLabel = this.notificationsState.badgeLabel;
  public readonly notificationsAriaLabel = this.notificationsState.bellAriaLabel;
  public readonly notificationsOpen = this.notificationsState.panelOpen;
  public readonly notificationsRingToken = this.notificationsState.ringToken;

  public readonly toggleSidebar = output<void>();

  public readonly viewSwitchSegments = VIEW_SWITCH_SEGMENTS;

  // CA04: durante a personificação o gestor não navega para fora do contexto do alvo. Diferente das
  // outras cinco superfícies, este controle não troca token nem chama select-profile — é redirecionamento
  // entre apps. A trava aqui é consistência de produto (decisão C3 do plano), não contenção de escalada.
  //
  // DEV-960: por isso mesmo o Administrador Lekto é isento dela. O sinal é `isManagerImpersonation` e não
  // `isImpersonating` — a trava existe porque QUEM personificou é gestão e não deve sair do contexto do
  // alvo; personificação iniciada pelo admin preserva o comportamento de produção dele (decisão D2) e o
  // alternador segue disponível, dentro do escopo do gestor personificado.
  //
  // CA01 (DEV-919): o gate de PAPEL vive em `canSwitchSystems` — só gestão vê o alternador. Os dois
  // fatores continuam independentes: ao personificar, `authProfile()` passa a ser o do alvo, então o
  // admin personificando um gestor satisfaz `canSwitchSystems()`, e personificando um professor não.
  public readonly showViewSwitch = computed<boolean>(
    () =>
      !this.impersonationService.isManagerImpersonation() &&
      this.canSwitchSystems() &&
      // DEV-966 (CA 05): a troca para o Admin também fica indisponível com o modo ligado.
      featureAvailability("view-switch", this.offlineState.enabled()) === "allowed"
  );

  public readonly viewSwitchActiveId = computed<string>(() => this.currentApp());

  public readonly resolvedLogoSrc = computed<string>(
    () => this.logoSrc() ?? this.themeState.logoPrimarySrc()
  );

  public closeSidebar(): void {
    this.toggleSidebar.emit();
  }

  /**
   * O parâmetro segue `string` de propósito: quem emite é `viewSwitchClick`, declarado
   * `output<string>()` (`sidebar-menu.component.ts:112`) sobre `ViewSwitchSegment.id`, também
   * `string` no design-system. Estreitar aqui para `AppSystem` não estreita a origem — só quebra a
   * checagem do template.
   *
   * O estreitamento fica explícito na linha abaixo, e mantém à risca o default anterior: só
   * `"admin"` vai para o admin, qualquer outro valor vai para a plataforma. É o oposto do
   * `toAppId` (que é fail-closed no admin), e é correto aqui — os segmentos são fixos em
   * `VIEW_SWITCH_SEGMENTS` e este é um controle de UI, não um destino decidido pelo servidor.
   */
  public onViewSwitchClick(targetApp: string): void {
    if (targetApp === this.currentApp()) {
      return;
    }
    const url = resolveAppUrl(targetApp === "admin" ? "admin" : "platform", this.productConfig);
    if (url) {
      globalThis.location.href = url;
    }
  }

  public onNotificationsClick(bell: HTMLElement): void {
    this.notificationsState.togglePanel(bell);
  }

  /**
   * Abrir qualquer outro painel do rail recolhe o de notificações. O `lkt-sidebar-menu` só sabe
   * fechar o que ele mesmo renderiza, e o painel de notificações mora no shell.
   */
  public override onHelpMenuExpand(): void {
    super.onHelpMenuExpand();
    this.notificationsState.closePanel();
  }
}
